import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../CustomHooks/useApi";
import * as XLSX from "xlsx";

const CashInRequestTable = () => {

    const userId = useSelector((state) => state.auth.employeeDetails?.userId);
    const { callApi: Cashinrequesttable } = useApi("/cashin/history", "GET");

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [downloading, setDownloading] = useState(false);

    const getToday = () => {
        return new Date().toISOString().split("T")[0];
    };

    const [fromDate, setFromDate] = useState(getToday());
    const [toDate, setToDate] = useState(getToday());

    /* ===== DOWNLOAD EXCEL ===== */
    const downloadExcel = async () => {
        try {
            setDownloading(true);

            let query = `?userId=${userId}`;
            if (fromDate) query += `&fromDate=${fromDate}`;
            if (toDate) query += `&toDate=${toDate}`;

            // 1. Hit the API directly
            const res = await Cashinrequesttable(null, query);

            if (res && Array.isArray(res) && res.length > 0) {
                // 2. Format the data for Excel
                const excelData = res.map((row, index) => ({
                    "Sr. No": index + 1,
                    "Amount": row.amount,
                    "Amount in Words": numberToWords(row.amount || 0),
                    "Request Date/Time": formatDateTime(row.requestDateTime),
                    "Deposit Date": row.depositDate,
                    "Mode": row.paymentMode,
                    "Bank Name": row.bankName,
                    "Approved Date": formatDateTime(row.approvedDate),
                    "Remarks": row.requesterRemarks,
                    "Status": row.status
                }));

                // 3. Generate and download the Excel file
                const worksheet = XLSX.utils.json_to_sheet(excelData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "CashIn Requests");

                const fileName = `CashIn_Requests_${fromDate}_to_${toDate}.xlsx`;
                XLSX.writeFile(workbook, fileName);
            } else {
                alert("No data available to download for the selected dates.");
            }
        } catch (error) {
            console.error("Download Error:", error);
            alert("Failed to download data.");
        } finally {
            setDownloading(false);
        }
    };

    /* ===== DATE FORMAT ===== */

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";

        const formatted = new Date(dateString.replace(" ", "T"));

        return formatted.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        });
    };

    /* ===== NUMBER TO WORDS ===== */

    const numberToWords = (num) => {

        const a = ["", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ",
            "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ",
            "Seventeen ", "Eighteen ", "Nineteen "];

        const b = ["", "", "Twenty ", "Thirty ", "Forty ", "Fifty ",
            "Sixty ", "Seventy ", "Eighty ", "Ninety "];

        const inWords = (n) => {

            if (n < 20) return a[n];

            if (n < 100)
                return b[Math.floor(n / 10)] + a[n % 10];

            if (n < 1000)
                return a[Math.floor(n / 100)] + "Hundred " + inWords(n % 100);

            if (n < 100000)
                return inWords(Math.floor(n / 1000)) + "Thousand " + inWords(n % 1000);

            if (n < 10000000)
                return inWords(Math.floor(n / 100000)) + "Lakh " + inWords(n % 100000);

            return inWords(Math.floor(n / 10000000)) + "Crore " + inWords(n % 10000000);
        };

        return inWords(num) + "Only";
    };

    /* ===== AMOUNT COLOR ===== */

    const getAmountColor = (amount) => {

        if (amount >= 1000000) return "text-purple-700";

        if (amount >= 400000) return "text-red-600";

        return "text-black";
    };

    /* ===== FETCH API ===== */

    const fetchRequests = async () => {

        try {

            setLoading(true);


            let query = `?userId=${userId}`;

            // ✅ Date bhi bhejna hai
            if (fromDate) query += `&fromDate=${fromDate}`;
            if (toDate) query += `&toDate=${toDate}`;

            const res = await Cashinrequesttable(null, query);

            if (res && Array.isArray(res)) {
                setData(res);
                setFilteredData(res);
            }

        } catch (error) {
            console.error("Fetch Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId && fromDate && toDate) {
            fetchRequests();
        }
    }, [fromDate, toDate]);

    const clearFilter = () => {
        const today = getToday();

        setFromDate(today);
        setToDate(today);
    };
    /* ===== MODE COLOR ===== */

    const getModeColor = (mode) => {

        if (mode === "Cash") return "text-green-600 font-semibold";

        if (mode === "CDM") return "text-blue-600 font-semibold";

        if (mode === "Online") return "text-indigo-600 font-semibold";

        return "";
    };

    /* ===== STATUS COLOR ===== */

    const getStatusStyle = (status) => {

        if (status === "REQUESTED")
            return "bg-yellow-100 text-yellow-700";

        if (status === "APPROVED")
            return "bg-green-100 text-green-700";

        if (status === "REJECTED")
            return "bg-red-100 text-red-700";

        return "";
    };

    return (
        <div className="w-full">

            <h2 className="text-xl font-bold text-[#0A2A4F] mb-4">
                Cash In Request Table
            </h2>

            {/* FILTER */}

            <div className="flex flex-wrap gap-3 mb-4">

                <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="border px-3 py-2 rounded-full"
                />

                <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="border px-3 py-2 rounded-full"
                />

                <button
                    onClick={clearFilter}
                    className="bg-gray-400 text-white px-5 py-2 rounded-full hover:bg-gray-500"
                >
                    Clear
                </button>

                <button
                    onClick={downloadExcel}
                    disabled={downloading}
                    className={`px-5 py-2 rounded-full font-semibold text-white transition 
                        ${downloading ? "bg-green-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
                >
                    {downloading ? "Downloading..." : "Download Excel"}
                </button>

            </div>

            {/* TABLE */}

            <div className="rounded-xl border border-gray-300 bg-white shadow-sm max-h-[70vh] overflow-y-auto">

                <table className="w-full text-[14px] border-collapse">

                    <thead className="bg-[#0A2A4F] sticky top-0">

                        <tr className="text-white text-left">
                            <th className="border px-3 py-2">Sr. No</th>
                            <th className="border px-3 py-2">Amount</th>
                            <th className="border px-3 py-2">Request Date/Time</th>
                            <th className="border px-3 py-2">Deposit Date</th>
                            <th className="border px-3 py-2">Mode</th>
                            <th className="border px-3 py-2">Bank Name</th>
                            <th className="border px-3 py-2">Approved Date</th>
                            <th className="border px-3 py-2">Remarks</th>
                            <th className="border px-3 py-2">Status</th>

                        </tr>

                    </thead>

                    <tbody>

                        {loading ? (

                            <tr>
                                <td colSpan="7" className="text-center py-8 text-gray-500">
                                    Loading...
                                </td>
                            </tr>

                        ) : filteredData.length > 0 ? (

                            filteredData.map((row, index) => (

                                <tr key={index} className="odd:bg-white even:bg-gray-50 hover:bg-blue-50">
                                    <td className="border px-2 py-2 w-12 text-center">
                                        {index + 1}
                                    </td>

                                    <td className={`border px-3 py-2 font-semibold ${getAmountColor(row.amount)}`}>

                                        <div>
                                            ₹{row.amount?.toLocaleString()}
                                        </div>

                                        <div className="text-xs font-bold">
                                            {numberToWords(row.amount || 0)}
                                        </div>

                                    </td>

                                    <td className="border px-3 py-2">
                                        {formatDateTime(row.requestDateTime)}
                                    </td>

                                    <td className="border px-3 py-2">
                                        {row.depositDate}
                                    </td>

                                    <td className={`border px-3 py-2 ${getModeColor(row.paymentMode)}`}>
                                        {row.paymentMode}
                                    </td>
                                    <td className="border px-3 py-2">
                                        {row.bankName}
                                    </td>

                                    <td className="border px-3 py-2">
                                        {formatDateTime(row.approvedDate)}
                                    </td>

                                    <td className="border px-3 py-2">
                                        {row.requesterRemarks}
                                    </td>

                                    <td className="border px-3 py-2">
                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusStyle(row.status)}`}>
                                            {row.status}
                                        </span>
                                    </td>

                                </tr>

                            ))

                        ) : (

                            <tr>
                                <td colSpan="7" className="text-center py-8 text-gray-500">
                                    No records found
                                </td>
                            </tr>

                        )}

                    </tbody>

                </table>

            </div>

        </div>
    );
};

export default CashInRequestTable;